import Modal from "@via/components/modal.tsx";
import { ButtonColor } from "@via/utils/constants.tsx";
import { type FC, ReactNode } from "react";

interface DeleteModalProps<T extends { id: string | number; name: string }> {
  item: T | null;
  setItem: (item: T | null) => void;
  /**
   * Forwarded straight to Modal's onConfirm, so it carries that contract: resolving
   * false keeps the dialog open, true or undefined closes it, and a rejection keeps
   * it open. Typed void alone until now, which left a consumer no way to say "do not
   * close" even though the dialog underneath already understood it.
   */
  deleteFunction: () => Promise<void | boolean>;
  isLoading: boolean;
  title?: string;
  message?: (name: string) => ReactNode;
  confirmText?: string;
  cancelText?: string;
  /** If true, hides the confirm button and leaves only the cancel button. */
  hideConfirmButton?: boolean;
}

const DeleteModal: FC<DeleteModalProps<any>> = ({
  item,
  setItem,
  deleteFunction,
  isLoading,
  title = "Delete Item",
  message = name => (
    <>
      Are you sure you want to delete{" "}
      <strong className="font-semibold">{name}</strong>?
    </>
  ),
  confirmText = "Delete",
  cancelText = "Close",
  hideConfirmButton = false,
}) => {
  return (
    <Modal
      open={!!item}
      setOpen={() => setItem(null)}
      title={title}
      confirmText={confirmText}
      cancelText={cancelText}
      confirmButtonColor={ButtonColor.Red}
      isConfirmLoading={isLoading}
      onConfirm={hideConfirmButton ? undefined : deleteFunction}>
      <div className="flex flex-col py-2 mb-6 text-sm leading-6 text-slate-600 md:py-3 lg:py-4">
        <p>{item && message(item.name)}</p>
      </div>
    </Modal>
  );
};

export default DeleteModal;
